'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Star, ThumbsUp } from 'lucide-react';

const reviews = [
{ id: 'rev-001', name: 'Priya Mehta', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1334e5402-1770349019845.png", avatarAlt: 'Smiling Indian woman with long dark hair', event: 'Wedding Reception', date: 'Mar 2026', rating: 5, text: 'Absolutely breathtaking venue. The chandeliers, the catering, the staff — everything was world-class. Our 650 guests were in awe. The coordination team handled every detail perfectly.', helpful: 24 },
{ id: 'rev-002', name: 'Karan Patel', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15c82c971-1763293909823.png", avatarAlt: 'Young Indian professional man with casual shirt', event: 'Corporate Gala', date: 'Feb 2026', rating: 5, text: 'Hosted our annual company gala here for 400 employees. The AV setup was flawless, catering was excellent, and the staff was incredibly professional. Will definitely book again.', helpful: 18 },
{ id: 'rev-003', name: 'Ananya Sharma', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_199c3ae7b-1772442873446.png", avatarAlt: 'Young Indian woman with shoulder-length hair and bright smile', event: 'Birthday Party', date: 'Jan 2026', rating: 4, text: 'Stunning venue for a milestone birthday. Only minor feedback: parking took a bit long at peak arrival time. But the hall itself and the food were outstanding. 4.5 stars really.', helpful: 11 },
{ id: 'rev-004', name: 'Vikram Nair', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf5d8c9a-1763300275719.png", avatarAlt: 'Indian man in formal attire with confident expression', event: 'Wedding Ceremony', date: 'Dec 2025', rating: 5, text: 'The Grand Meridian truly lives up to its name. We had a 700-guest wedding here and it was flawless from start to finish. The décor team transformed the hall beyond our imagination.', helpful: 31 },
{ id: 'rev-005', name: 'Sunita Rao', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_123863fef-1763300606145.png", avatarAlt: 'Professional Indian woman with glasses and business attire', event: 'Product Launch', date: 'Nov 2025', rating: 5, text: 'Used the venue for a major product launch with live streaming. The tech team was incredibly helpful setting up the 4K projection and remote audience feed. Zero technical issues throughout.', helpful: 15 },
{ id: 'rev-006', name: 'Rohit Gupta', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1de5715ae-1772163352622.png", avatarAlt: 'Indian man smiling warmly in casual shirt outdoors', event: 'Anniversary Celebration', date: 'Oct 2025', rating: 4, text: 'Beautiful venue for our 25th anniversary party. The outdoor lawn area was especially magical with the fairy lights. Would recommend the full-day booking — the hourly felt rushed.', helpful: 9 }];


const ratingBreakdown = [
{ stars: 5, count: 248, pct: 79 },
{ stars: 4, count: 46, pct: 15 },
{ stars: 3, count: 12, pct: 4 },
{ stars: 2, count: 4, pct: 1 },
{ stars: 1, count: 2, pct: 1 }];


export default function VenueReviews() {
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());
  const [visibleCount, setVisibleCount] = useState(4);

  const toggleHelpful = (id: string) => {
    setHelpfulClicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-bold text-gray-900 text-xl">Guest Reviews</h2>
        <div className="flex items-center gap-2">
          <Star size={18} className="fill-yellow-400 text-yellow-400" />
          <span className="font-extrabold text-gray-900 text-xl font-tabular">4.9</span>
          <span className="text-gray-400 text-sm">(312 reviews)</span>
        </div>
      </div>

      {/* Rating Breakdown */}
      <div className="mb-8 p-4 bg-gray-50 rounded-xl">
        <div className="flex flex-col gap-2">
          {ratingBreakdown.map((item) =>
          <div key={`breakdown-${item.stars}`} className="flex items-center gap-3">
              <div className="flex items-center gap-1 w-16 shrink-0">
                <span className="text-xs font-semibold text-gray-700">{item.stars}</span>
                <Star size={11} className="fill-yellow-400 text-yellow-400" />
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                className="bg-yellow-400 h-2 rounded-full"
                style={{ width: `${item.pct}%` }} />
              
              </div>
              <span className="text-xs text-gray-500 w-8 text-right font-tabular">{item.count}</span>
            </div>
          )}
        </div>
      </div>

      {/* Reviews List */}
      <div className="flex flex-col gap-6">
        {reviews.slice(0, visibleCount).map((review) =>
        <div key={review.id} className="border-b border-gray-50 pb-6 last:border-0 last:pb-0">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                  <AppImage src={review.avatar} alt={review.avatarAlt} width={40} height={40} className="object-cover w-full h-full" />
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm">{review.name}</div>
                  <div className="text-xs text-gray-400">{review.event} · {review.date}</div>
                </div>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                {Array.from({ length: 5 }).map((_, i) =>
              <Star key={`rev-star-${review.id}-${i}`} size={12} className={i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />
              )}
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">{review.text}</p>
            <button
            onClick={() => toggleHelpful(review.id)}
            className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
            helpfulClicked.has(review.id) ? 'text-red-600' : 'text-gray-400 hover:text-gray-600'}`
            }>
            
              <ThumbsUp size={13} />
              Helpful ({helpfulClicked.has(review.id) ? review.helpful + 1 : review.helpful})
            </button>
          </div>
        )}
      </div>

      {visibleCount < reviews.length &&
      <button
        onClick={() => setVisibleCount((v) => v + 4)}
        className="mt-6 w-full py-3 border-2 border-gray-200 hover:border-red-300 hover:text-red-600 text-gray-700 font-semibold text-sm rounded-xl transition-all">
        
          Show more reviews ({reviews.length - visibleCount} remaining)
        </button>
      }
    </div>);

}